/**
 * HomePage.tsx
 * Página de inicio PTEL Tool - Normalización de coordenadas para emergencias
 *
 * @version 1.0.0
 * @description Orquestador de secciones: estado, efectos, handlers y composición.
 *   Toda la presentación delegada en home/sections/.
 *   Datos y constantes centralizados en home/data/.
 *   Iconos en home/icons/.
 */

import { useState, useEffect, useCallback, useRef, type FC, type DragEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import {
  HeroSection,
  PipelineSection,
  FeaturesSection,
  HorizonSection,
  SourcesSection,
  TransparencySection,
  CtaSection,
  FooterSection,
} from './sections';
import type { DropError } from './sections/HeroSection';
// PORT 26-ago: la puerta de la portada valida con el mismo predicado que el wizard.
import { isAcceptedExtension } from '../../lib/acceptedFormats';
import {
  AUTH_KEY, AUTH_USER_KEY, AUTH_DATE_KEY, THEME_KEY,
  DOCUMENTS,
} from './data';
import './home-v2.css';

// ============================================
// COMPONENT
// ============================================

interface HomePageProps {
  onOpenConversor?: () => void;
}

export const HomePage: FC<HomePageProps> = ({ onOpenConversor }) => {
  const navigate = useNavigate();
  const { setTheme, notifyAuth, openLoginModal, registerLogout, isAuthenticated: ctxAuthenticated } = useAppContext();

  // State
  const [isDragging, setIsDragging] = useState(false);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [authenticated, setAuthenticated] = useState(false);
  const [dropFile, setDropFile]   = useState<File | null>(null);
  const [dropError, setDropError] = useState<DropError | null>(null);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================
  // EFFECTS
  // ============================================

  // Initialize theme + auth state
  useEffect(() => {
    // H-01 (revisado): forzar dark solo en primera visita sin preferencia guardada.
    // Si el usuario está en modo claro y navega a Home, se respeta su elección.
    const savedTheme = localStorage.getItem(THEME_KEY) as 'dark' | 'light' | null;
     
    if (savedTheme !== 'light') setTheme('dark');
    // Sincronizar estado de autenticación desde localStorage
    const isAuth = localStorage.getItem(AUTH_KEY) === 'true';
    const authDate = localStorage.getItem(AUTH_DATE_KEY);
    const today = new Date().toISOString().split('T')[0];
    const auth = isAuth && authDate === today;
     
    setAuthenticated(auth);
    notifyAuth(auth, auth ? (localStorage.getItem(AUTH_USER_KEY) ?? undefined) : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: notifyAuth/setTheme estables; re-ejecutar causaría bucle de auth
  }, []);

  // Handle scroll from FaqPage navigation via sessionStorage
  useEffect(() => {
    const sectionId = sessionStorage.getItem('scrollTo');
    if (sectionId) {
      sessionStorage.removeItem('scrollTo');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // ============================================
  // CAROUSEL
  // PipelineSection gestiona el avance internamente.
  // HomePage solo expone el setter de índice para los botones de municipio.
  // ============================================

  const handleDotClick = useCallback((index: number) => {
    setCurrentDocIndex(index);
  }, []);

  // ============================================
  // AUTH HANDLERS
  // ============================================

  const isAuthenticatedToday = useCallback((): boolean => {
    const isAuth = localStorage.getItem(AUTH_KEY) === 'true';
    const authDate = localStorage.getItem(AUTH_DATE_KEY);
    const today = new Date().toISOString().split('T')[0];
    return isAuth && authDate === today;
  }, []);

  const handleFileSelect = useCallback(() => {
    if (isAuthenticatedToday()) {
      fileInputRef.current?.click();
    } else {
      openLoginModal();
    }
  }, [isAuthenticatedToday, openLoginModal]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_DATE_KEY);
    setAuthenticated(false);
    notifyAuth(false);             // ← sincroniza el header global
  }, [notifyAuth]);

  // Registrar callback de logout en AppContext para que AppShell pueda ejecutarlo
  useEffect(() => {
    registerLogout(handleLogout);
  }, [registerLogout, handleLogout]);

  // ============================================
  // FILE HANDLERS
  // ============================================

  // PORT 26-ago · `OBS-P1-PORTADA-ANUNCIA-NUEVE-Y-ADMITE-CUATRO-SIN-PARSER`.
  // Aquí había un `VALID_EXTS` propio con los 9 formatos históricos: dejaba
  // pasar .dbf/.geojson/.kml/.tsv hasta el wizard, que los rechazaba después
  // de que la portada hubiera dicho dos veces que el fichero valía. Ahora la
  // puerta usa el mismo predicado que el wizard.
  const validateFile = useCallback((file: File): DropError | null => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!isAcceptedExtension(ext)) return { type: 'format', ext: '.' + ext };
    return null;
  }, []);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { setDropError(err); return; }
    setDropFile(file); setDropError(null);
  }, [validateFile]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!isAuthenticatedToday()) { openLoginModal(); return; }
    const files = e.dataTransfer.files;
    if (files.length > 1) { setDropError({ type: 'multiple' }); return; }
    const file = files[0]; if (!file) return;
    const err = validateFile(file);
    if (err) { setDropError(err); return; }
    setDropFile(file); setDropError(null);
  }, [isAuthenticatedToday, openLoginModal, validateFile]);

  const handleStartNormalization = useCallback(() => {
    if (!dropFile) return;
    navigate('/wizard', { state: { file: dropFile } });
  }, [dropFile, navigate]);

  const handleReset = useCallback(() => {
    setDropFile(null);
    setDropError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [fileInputRef]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="home-page">
      {/* HeaderSection eliminado: vive de forma persistente en App.tsx → sin salto al navegar */}

      <HeroSection
        isDragging={isDragging}
        fileInputRef={fileInputRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onFileChange={handleFileChange}
        isAuthenticated={ctxAuthenticated || authenticated}
        onLoginClick={openLoginModal}
        dropFile={dropFile}
        dropError={dropError}
        onStartNormalization={handleStartNormalization}
        onReset={handleReset}
        onFileSelect={handleFileSelect}
      />

      <PipelineSection
        currentDocIndex={currentDocIndex}
        onDotClick={handleDotClick}
        totalDocs={DOCUMENTS.length}
      />

      <FeaturesSection />

      <HorizonSection />

      <SourcesSection />
      <TransparencySection />
      <CtaSection onFileSelect={handleFileSelect} />
      <FooterSection onOpenConversor={onOpenConversor} />
    </div>
  );
};

export default HomePage;
